import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/Button';
import { CheckCircle2, ShieldCheck, Lock, Award, ArrowRight } from 'lucide-react';
import { trackAddToCart } from '../../hooks/useMetaPixel';
export function HeroSection() {
  const handleScrollToPricing = () => {
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      pricingSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <section className="bg-brand-cream min-h-screen flex items-center relative overflow-hidden pt-12 pb-16 md:pt-20 md:pb-24" data-id="element-130">
      {/* Subtle background elements for a premium, calm feel */}
      <div className="absolute inset-0 overflow-hidden -z-10 pointer-events-none" data-id="element-131">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-brand-sage/10 blur-3xl" data-id="element-132" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] rounded-full bg-brand-gold/5 blur-3xl" data-id="element-133" />
      </div>

      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8" data-id="element-134">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-135">
          {/* Text Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className="max-w-2xl mx-auto lg:mx-0 flex flex-col order-1" data-id="element-136">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-serif font-bold text-brand-charcoal-dark leading-[1.1] mb-6 tracking-tight" data-id="element-137">
              ¿Ansiedad, barriga inflamada y{' '}
              <span className="text-brand-gold" data-id="element-138">cansancio constante</span>?
            </h1>

            <p className="text-xl sm:text-2xl text-brand-charcoal-dark font-semibold mb-4 leading-snug" data-id="element-139">
              Puede ser cortisol alto.
            </p>

            <p className="text-xl sm:text-2xl text-brand-charcoal/80 mb-6 font-light leading-relaxed tracking-wide" data-id="element-140">
              Descubre el protocolo simple que ayuda a recuperar calma, energía
              y descanso.
            </p>

            <p className="text-2xl sm:text-3xl font-bold text-brand-gold-dark mb-10" data-id="element-141">
              🔥 Hoy solo $6.36
            </p>

            <ul className="space-y-5 mb-12" data-id="element-142">
              {['Apoya el bienestar diario', 'Ideal para rutinas de equilibrio y calma', 'Fácil de incorporar a tu día'].map((bullet, idx) => <motion.li key={idx} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.5,
              delay: 0.3 + idx * 0.1
            }} className="flex items-start" data-id="element-143">
                  <CheckCircle2 className="h-6 w-6 text-brand-gold shrink-0 mr-4 mt-0.5" data-id="element-144" />
                  <span className="text-brand-charcoal/90 font-medium text-lg sm:text-xl" data-id="element-145">
                    {bullet}
                  </span>
                </motion.li>)}
            </ul>

            <div className="flex flex-col sm:flex-row gap-4 mb-6" data-id="element-146">
              <Button size="lg" variant="primary" className="w-full sm:w-auto text-lg font-bold shadow-2xl shadow-brand-gold/30 py-6 px-8" onClick={handleScrollToPricing} data-id="element-147">
                Quiero Recuperar Mi Enfoque
                <ArrowRight className="ml-2 h-5 w-5" data-id="element-148" />
              </Button>
            </div>

            <p className="text-sm text-brand-charcoal/60 mb-10 text-center sm:text-left flex sm:justify-start flex-row items-center justify-center tracking-wide uppercase font-medium" data-id="element-149">
              Compra segura a través de Shopify y Hotmart
            </p>

            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-8 gap-y-4 pt-8 border-t border-brand-gold/20" data-id="element-150">
              <div className="flex items-center text-sm font-semibold text-brand-charcoal/80 uppercase tracking-wider" data-id="element-151">
                <Lock className="h-4 w-4 text-brand-gold mr-2 shrink-0" data-id="element-152" />
                Pago seguro
              </div>
              <div className="flex items-center text-sm font-semibold text-brand-charcoal/80 uppercase tracking-wider" data-id="element-153">
                <ShieldCheck className="h-4 w-4 text-brand-gold mr-2 shrink-0" data-id="element-154" />
                Checkout protegido
              </div>
              <div className="flex items-center text-sm font-semibold text-brand-charcoal/80 uppercase tracking-wider" data-id="element-155">
                <Award className="h-4 w-4 text-brand-gold mr-2 shrink-0" data-id="element-156" />
                Compra confiable
              </div>
            </div>
          </motion.div>

          {/* Image Content */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: 'easeOut'
        }} className="relative mx-auto w-full max-w-lg lg:max-w-none order-2" data-id="element-157">
            {/* Premium decorative backdrop */}
            <div className="absolute inset-0 bg-gradient-to-tr from-brand-sage/20 to-brand-cream-dark rounded-3xl transform rotate-3 scale-105 -z-10 transition-transform duration-700 hover:rotate-6" data-id="element-158"></div>

            <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/60 bg-white/40 backdrop-blur-sm" data-id="element-159">
              <img src="https://cdn.magicpatterns.com/uploads/2K1UcLURyYbQtRxYwPpaK5/Image_Landing_Page1.png" alt="Emprendedora enfocada trabajando con calma y equilibrio" className="w-full h-auto rounded-3xl" data-id="element-160" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}